import { LikeMeta, likeMetaStore, likeStatusStore } from '../../store/LikeStore';
import { MAX_STORE_ITEMS } from './constants';
import { createLikeStatusRecord } from './helpers';
import { orderStore } from './orderStore';
import { ProductWithSavedStatus } from './types';

/**
 * 기본 방식으로 Like IDs를 초기화합니다.
 */
export function initLikeIds(ids: string[], groupOrderId?: number, shouldSkipApiCall?: boolean): string[] {
  const resolvedGroupOrderId = groupOrderId ?? orderStore.getGroupOrder();

  likeMetaStore.setState((prev) => {
    return ids?.reduce<LikeMeta>((acc, id) => {
      const isExisting = !!acc[id];
      const hasValue = shouldSkipApiCall ? false : isExisting ? acc[id].has : true;

      acc[id] = {
        has: hasValue,
        inView: false,
        order: orderStore.getOrder(),
        groupOrder: resolvedGroupOrderId,
        id,
        shouldSkipApiCall,
      };

      if (!isExisting) {
        orderStore.incOrder();
      }

      return acc;
    }, prev);
  });

  // shouldSkipApiCall이 true일 때는 직접 likeStatusStore에 찜 상태를 설정
  if (shouldSkipApiCall) {
    const likeStatus: Record<string, boolean> = {};
    ids.forEach((id) => {
      likeStatus[id] = true;
    });
    likeStatusStore.setState(likeStatus);
  }

  if (typeof groupOrderId === 'undefined') {
    orderStore.incGroupOrder();
  }

  return ids;
}

/**
 * is_saved_product 필드를 기반으로 Like 상태를 초기화합니다.
 */
export function initLikeIdsWithSavedProduct<T extends ProductWithSavedStatus>(
  ids: string[],
  data: T[],
  groupOrderId: number,
): void {
  // likeMetaStore 업데이트
  likeMetaStore.setState((prev) => {
    return ids.reduce<LikeMeta>((acc, id, index) => {
      const isExisting = !!acc[id];
      const item = data[index];
      const hasValue = item?.is_saved_product ?? false;

      acc[id] = {
        has: hasValue,
        inView: false,
        order: orderStore.getOrder(),
        groupOrder: groupOrderId,
        id,
        shouldSkipApiCall: true,
        wasInitializedWithSavedProduct: true,
      };

      if (!isExisting) {
        orderStore.incOrder();
      }

      return acc;
    }, prev);
  });

  // likeStatusStore 업데이트
  const likeStatus = createLikeStatusRecord(ids, data);
  likeStatusStore.setState(likeStatus);
}

/**
 * 스토어 정리를 수행합니다.
 */
export function cleanupLikeMetaStore(groupOrderId: number): void {
  likeMetaStore.setState((prev) => {
    const isOverCount = Object.keys(prev).length > MAX_STORE_ITEMS;
    let idToRemove: number | undefined;

    if (isOverCount) {
      idToRemove = orderStore.getIdsToRemove();
    }

    for (const id in prev) {
      const prevItem = prev[id];
      prevItem.inView = false;
      if (idToRemove === prevItem.groupOrder) {
        delete prev[id];
      }
    }

    return prev;
  });
}
